import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, IconButton, Divider, Checkbox,
  TextField, Tooltip, CircularProgress, Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import RouteIcon from '@mui/icons-material/Route';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlaceIcon from '@mui/icons-material/Place';
import type { TripPlan, TripAttraction } from '../../services/tripService';
import { TRANSPORT_OPTIONS } from '../../services/tripService';
import type { DayForecast } from '../../services/attractionService';
import { fetchWeatherForDate } from '../../services/attractionService';

// ── Weather strip for a date group ───────────────────────────────────────────
const DayWeather: React.FC<{ date: string; lat: number; lng: number }> = ({ date, lat, lng }) => {
  const [forecast, setForecast] = useState<DayForecast[] | null>(null);

  useEffect(() => {
    setForecast(null);
    fetchWeatherForDate(lat, lng, date, 7)
      .then(setForecast)
      .catch(() => setForecast([]));
  }, [lat, lng, date]);

  if (forecast === null) return (
    <div style={{ padding: '2px 12px 4px' }}>
      <CircularProgress size={10} sx={{ color: 'rgba(255,255,255,0.3)' }} />
    </div>
  );
  if (forecast.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: 2, paddingLeft: 12, paddingBottom: 4 }}>
      {forecast.slice(0, 7).map(d => (
        <Tooltip key={d.date} title={`${d.date}  ${d.tempMax}°/${d.tempMin}°`} arrow>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            minWidth: 26, padding: '1px 2px',
            background: 'rgba(255,255,255,0.05)', borderRadius: 3,
          }}>
            <span style={{ fontSize: 12, lineHeight: 1 }}>{d.emoji}</span>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)' }}>{d.tempMax}°</span>
          </div>
        </Tooltip>
      ))}
    </div>
  );
};

// ── Main panel ────────────────────────────────────────────────────────────────
interface Props {
  plans: TripPlan[];
  visiblePlanIds: string[];
  lang: string;
  onPlansChange: (plans: TripPlan[]) => void;
  onVisibilityChange: (ids: string[]) => void;
  onClose: () => void;
}

const TripPanel: React.FC<Props> = ({
  plans, visiblePlanIds, lang, onPlansChange, onVisibilityChange, onClose,
}) => {
  const isZh = !lang.startsWith('en');

  const [newPlanName, setNewPlanName]   = useState('');
  const [editPlanId, setEditPlanId]     = useState<string | null>(null);
  const [editPlanName, setEditPlanName] = useState('');
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);
  const [expandedDates, setExpandedDates]   = useState<string[]>([]);  // "planId:date"

  const toggleDateExpand = (key: string) =>
    setExpandedDates(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );

  const handleCreatePlan = () => {
    const name = newPlanName.trim();
    if (!name) return;
    const plan: TripPlan = { id: `plan_${Date.now()}`, name, attractions: [], createdAt: Date.now() };
    onPlansChange([...plans, plan]);
    onVisibilityChange([...visiblePlanIds, plan.id]);
    setNewPlanName('');
  };

  const handleDeletePlan = (id: string) => {
    onPlansChange(plans.filter(p => p.id !== id));
    onVisibilityChange(visiblePlanIds.filter(i => i !== id));
    if (expandedPlanId === id) setExpandedPlanId(null);
  };

  const handleRenameConfirm = () => {
    if (!editPlanId) return;
    const name = editPlanName.trim();
    if (name) onPlansChange(plans.map(p => p.id === editPlanId ? { ...p, name } : p));
    setEditPlanId(null);
  };

  const handleRemoveAttraction = (planId: string, attrId: string, date: string) => {
    onPlansChange(plans.map(p =>
      p.id !== planId ? p : {
        ...p,
        attractions: p.attractions.filter(a => !(a.id === attrId && a.date === date)),
      }
    ));
  };

  const toggleVisible = (id: string) =>
    onVisibilityChange(
      visiblePlanIds.includes(id)
        ? visiblePlanIds.filter(i => i !== id)
        : [...visiblePlanIds, id]
    );

  return (
    <Paper elevation={6} sx={{
      position: 'absolute', left: 74, top: 10,
      width: 310, borderRadius: 3, zIndex: 12,
      background: 'rgba(12,20,45,0.96)', backdropFilter: 'blur(12px)',
      border: '1px solid rgba(155,89,182,0.45)', color: '#fff',
      display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 24px)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <RouteIcon sx={{ color: '#c39bd3', fontSize: 18 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#c39bd3', flex: 1 }}>
          {isZh ? '旅程計畫' : 'Trip Plans'}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Plans */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {plans.length === 0 ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, p: 2, textAlign: 'center' }}>
            {isZh ? '尚無旅程計畫，請新增' : 'No trip plans yet. Create one below.'}
          </Typography>
        ) : plans.map((plan, idx) => {
          // Group attractions by date
          const dateMap = new Map<string, TripAttraction[]>();
          for (const a of (plan.attractions ?? [])) {
            const list = dateMap.get(a.date) ?? [];
            list.push(a);
            dateMap.set(a.date, list);
          }
          const sortedDates = Array.from(dateMap.keys()).sort();
          const totalSpots = (plan.attractions ?? []).length;

          return (
            <React.Fragment key={plan.id}>
              {idx > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />}

              {/* Plan header */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '4px 6px 4px 4px' }}>
                <Checkbox
                  checked={visiblePlanIds.includes(plan.id)}
                  onChange={() => toggleVisible(plan.id)}
                  size="small"
                  sx={{ color: 'rgba(155,89,182,0.4)', '&.Mui-checked': { color: '#9b59b6' }, p: '4px' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editPlanId === plan.id ? (
                    <TextField
                      value={editPlanName}
                      onChange={e => setEditPlanName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleRenameConfirm();
                        if (e.key === 'Escape') setEditPlanId(null);
                      }}
                      size="small" autoFocus variant="standard"
                      InputProps={{ style: { color: '#fff', fontSize: 13 } }}
                      sx={{ '& .MuiInput-underline:after': { borderColor: '#9b59b6' } }}
                    />
                  ) : (
                    <div>
                      <Typography sx={{ fontSize: 13, color: '#fff', lineHeight: 1.3 }} noWrap>
                        {plan.name}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
                        {sortedDates.length} {isZh ? '天' : 'days'} · {totalSpots} {isZh ? '景點' : 'spots'}
                      </Typography>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  {editPlanId !== plan.id && (
                    <Tooltip title={expandedPlanId === plan.id ? (isZh ? '收合' : 'Collapse') : (isZh ? '展開' : 'Expand')}>
                      <IconButton size="small"
                        onClick={() => setExpandedPlanId(expandedPlanId === plan.id ? null : plan.id)}
                        sx={{ color: 'rgba(255,255,255,0.35)', p: '3px', '&:hover': { color: '#c39bd3' } }}>
                        {expandedPlanId === plan.id
                          ? <ExpandLessIcon sx={{ fontSize: 15 }} />
                          : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
                      </IconButton>
                    </Tooltip>
                  )}
                  {editPlanId === plan.id ? (
                    <Tooltip title={isZh ? '確認' : 'Confirm'}>
                      <IconButton size="small" onClick={handleRenameConfirm} sx={{ color: '#9b59b6', p: '3px' }}>
                        <CheckIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title={isZh ? '重新命名' : 'Rename'}>
                      <IconButton size="small" onClick={() => { setEditPlanId(plan.id); setEditPlanName(plan.name); }}
                        sx={{ color: 'rgba(255,255,255,0.35)', p: '3px', '&:hover': { color: '#c39bd3' } }}>
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={isZh ? '刪除計畫' : 'Delete plan'}>
                    <IconButton size="small" onClick={() => handleDeletePlan(plan.id)}
                      sx={{ color: 'rgba(255,100,100,0.45)', p: '3px', '&:hover': { color: '#ff6b6b' } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>

              {/* Date groups */}
              <Collapse in={expandedPlanId === plan.id} unmountOnExit>
                <div style={{ paddingBottom: 6 }}>
                  {sortedDates.length === 0 ? (
                    <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', px: 2, pb: 1 }}>
                      {isZh ? '在地圖上右鍵景點加入此計畫' : 'Right-click a spot on the map to add to this plan'}
                    </Typography>
                  ) : sortedDates.map(date => {
                    const attrs = dateMap.get(date)!;
                    const first = attrs[0];
                    const groupKey = `${plan.id}:${date}`;
                    const expanded = expandedDates.includes(groupKey);

                    return (
                      <div key={date} style={{ margin: '2px 6px 2px 12px' }}>
                        {/* Date group header */}
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          background: 'rgba(255,255,255,0.04)', borderRadius: 4,
                          padding: '3px 4px 3px 8px',
                        }}>
                          <Typography sx={{ fontSize: 11, color: '#c39bd3', flex: 1 }} noWrap>
                            📅 {date}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}>
                            {attrs.length} {isZh ? '個' : 'spots'}
                          </Typography>
                          <Tooltip title={expanded ? (isZh ? '收合' : 'Collapse') : (isZh ? '展開' : 'Expand')}>
                            <IconButton size="small" onClick={() => toggleDateExpand(groupKey)}
                              sx={{ color: 'rgba(255,255,255,0.35)', p: '2px', '&:hover': { color: '#c39bd3' } }}>
                              {expanded ? <ExpandLessIcon sx={{ fontSize: 13 }} /> : <ExpandMoreIcon sx={{ fontSize: 13 }} />}
                            </IconButton>
                          </Tooltip>
                        </div>

                        {/* Attractions + weather */}
                        <Collapse in={expanded} unmountOnExit>
                          <div style={{ paddingTop: 2 }}>
                            <DayWeather date={date} lat={first.lat} lng={first.lng} />
                            {attrs.map(attr => {
                              const transport = TRANSPORT_OPTIONS.find(o => o.key === attr.transport);
                              const aName = isZh ? (attr.nameZH || attr.nameEN) : (attr.nameEN || attr.nameZH);
                              return (
                                <div key={`${attr.id}-${date}`}
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: 12, paddingRight: 4, marginBottom: 2 }}>
                                  <PlaceIcon sx={{ fontSize: 10, color: '#9b59b6', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: 11, color: '#ddd', flex: 1 }} noWrap title={aName}>
                                    {aName}
                                  </Typography>
                                  {transport && (
                                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>
                                      {transport.emoji}
                                    </span>
                                  )}
                                  <Tooltip title={isZh ? '移除' : 'Remove'}>
                                    <IconButton size="small"
                                      onClick={() => handleRemoveAttraction(plan.id, attr.id, date)}
                                      sx={{ p: '1px', color: 'rgba(255,100,100,0.35)', flexShrink: 0, '&:hover': { color: '#ff6b6b' } }}>
                                      <DeleteOutlineIcon sx={{ fontSize: 11 }} />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              );
                            })}
                          </div>
                        </Collapse>
                      </div>
                    );
                  })}
                </div>
              </Collapse>
            </React.Fragment>
          );
        })}
      </div>

      {/* Create plan footer */}
      <div style={{ padding: '8px 10px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 6 }}>
        <TextField
          value={newPlanName}
          onChange={e => setNewPlanName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleCreatePlan(); }}
          placeholder={isZh ? '新旅程計畫名稱…' : 'New trip plan name…'}
          size="small" fullWidth variant="outlined"
          InputProps={{ style: { color: '#fff', fontSize: 13 } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: 'rgba(155,89,182,0.35)' },
              '&:hover fieldset': { borderColor: 'rgba(155,89,182,0.6)' },
              '&.Mui-focused fieldset': { borderColor: '#9b59b6' },
            },
          }}
        />
        <Tooltip title={isZh ? '新增計畫' : 'Create plan'}>
          <IconButton
            onClick={handleCreatePlan}
            disabled={!newPlanName.trim()}
            sx={{
              color: newPlanName.trim() ? '#9b59b6' : 'rgba(155,89,182,0.25)',
              border: '1px solid rgba(155,89,182,0.35)',
              borderRadius: 2, p: '6px',
              '&:hover': { background: 'rgba(155,89,182,0.15)' },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    </Paper>
  );
};

export default TripPanel;
